/**
 * CustomerGroupDetailPage - Detailansicht/Neuanlage einer Kundengruppe
 * 
 * Getrennte Ansichten für Kundengruppen vs Sammelposten:
 * - mode="customer-groups": Vollständige Bearbeitung inkl. Kunden-Zuordnung
 * - mode="system-buckets": Bearbeitung ohne Kunden-Zuordnung, mit systemCode
 * 
 * UX-Leitlinie: Kein Modal für Create/Edit, nur für Confirm
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, ArrowLeft, RefreshCw, Save, Trash2, ExternalLink, AlertTriangle, Copy, ChevronDown, ChevronRight, Package, Info } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

export type CustomerGroupDetailMode = 'customer-groups' | 'system-buckets';

interface CustomerGroup {
  id: string;
  code: string;
  name: string;
  type: string;
  systemCode: string | null;
  isActive: boolean;
  customers: { id: string; name: string }[];
}

interface FormData {
  name: string;
  systemCode: string;
  sortOrder: number;
}

const INITIAL_FORM: FormData = {
  name: '',
  systemCode: '',
  sortOrder: 0,
};

interface Props {
  mode: CustomerGroupDetailMode;
}

export default function CustomerGroupDetailPage({ mode }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  // Derived state based on mode
  const isSystemBucketMode = mode === 'system-buckets';
  const baseRoute = isSystemBucketMode ? '/admin/system-buckets' : '/admin/customer-groups';
  
  // All hooks must be before any conditional returns
  const [group, setGroup] = useState<CustomerGroup | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Technical section collapsed state
  const [techOpen, setTechOpen] = useState(false);
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // (Redirect entfernt - SYSTEM_BUCKET Create ist jetzt erlaubt)

  // Load group details (only for edit mode)
  const loadGroup = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/customer-groups/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setGroup(data);
        setFormData({
          name: data.name,
          systemCode: data.systemCode || '',
          sortOrder: data.sortOrder || 0,
        });
      } else if (res.status === 404) {
        navigate(baseRoute);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Kundengruppe:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isNew) {
      loadGroup();
    }
  }, [id, isNew]);
  

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    
    // SYSTEM_BUCKET: systemCode ist Pflicht
    if (isNew && isSystemBucketMode && !formData.systemCode.trim()) {
      setError('System-Code ist für Sammelposten erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/customer-groups' : `/api/admin/customer-groups/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim(),
          ...(isNew && isSystemBucketMode && {
            type: 'SYSTEM_BUCKET',
            systemCode: formData.systemCode.trim().toUpperCase(),
          }),
          ...(formData.sortOrder !== 0 && { sortOrder: formData.sortOrder }),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isNew) {
          // Nach Create zur korrekten Detail-Route navigieren
          navigate(`${baseRoute}/${data.id}`);
        } else {
          loadGroup();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/customer-groups/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate(baseRoute);
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !group) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Kundengruppe nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to={baseRoute} 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              {isSystemBucketMode ? <Package className="w-6 h-6" /> : <Users className="w-6 h-6" />}
              {isNew ? (isSystemBucketMode ? 'Neuer Sammelposten' : 'Neue Kundengruppe') : group?.name}
              {!isNew && group && (
                <span className="text-sm font-mono text-slate-400">({group.code})</span>
              )}
            </h1>
            {!isNew && group && !isSystemBucketMode && group.type !== 'SYSTEM_BUCKET' && (
              <p className="text-sm text-slate-500 mt-1">
                {group.customers.length} Kunden zugeordnet
                {!group.isActive && <span className="ml-2 text-red-500">(inaktiv)</span>}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadGroup()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={isSystemBucketMode ? 'z.B. Bewohnerwäsche' : 'z.B. Bansin'}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
          </section>

          {/* Organisation / Kunden - nur bei Kundengruppen-Modus und COLLECTIVE/RESIDENT Typ */}
          {!isNew && group && !isSystemBucketMode && group.type !== 'SYSTEM_BUCKET' && (
            <section className="bg-white rounded-lg border p-6 space-y-4">
              <h2 className="font-semibold text-slate-900 border-b pb-2">Zugeordnete Kunden</h2>
              
              <div className="text-center py-4">
                <div className="text-4xl font-bold text-slate-900">{group.customers.length}</div>
                <div className="text-sm text-slate-500 mt-1">Kunden in dieser Gruppe</div>
              </div>
              
              <Link
                to={`/admin/customers?group=${id}`}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Kunden anzeigen
              </Link>
              
              <p className="text-xs text-slate-500 text-center">
                Die Mitgliederpflege erfolgt in der Kundenansicht
              </p>
            </section>
          )}

          {/* Info bei Sammelposten-Modus */}
          {!isNew && group && isSystemBucketMode && (
            <section className="bg-orange-50 rounded-lg border border-orange-200 p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
                <div>
                  <h2 className="font-semibold text-orange-700">Sammelposten</h2>
                  <p className="text-sm text-orange-700 mt-2">
                    Sammelposten sind Produktionskategorien für die WSA-Erfassung. 
                    Kunden werden hier <strong>nicht</strong> fest zugeordnet – stattdessen werden 
                    bei der Erfassung die beteiligten Kunden angegeben.
                  </p>
                </div>
              </div>
              {group.systemCode && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-orange-200">
                  <span className="text-sm text-orange-600">System-Code:</span>
                  <code className="text-sm font-mono bg-orange-100 px-2 py-1 rounded">{group.systemCode}</code>
                </div>
              )}
            </section>
          )}

          {/* Hinweise (nur bei Edit, nicht bei Sammelposten) */}
          {!isNew && !isSystemBucketMode && group?.type !== 'SYSTEM_BUCKET' && (
            <section className="bg-slate-50 rounded-lg border p-6 space-y-4">
              <h2 className="font-semibold text-slate-700">Hinweise</h2>
              <p className="text-sm text-slate-600">
                Änderungen an der Kundengruppe wirken sich nicht auf bereits erfasste Waschvorgänge aus. 
                Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
              </p>
            </section>
          )}

          {/* Technisch (nur bei Edit) */}
          {!isNew && group && (
            <section className="bg-white rounded-lg border p-4">
              <button
                onClick={() => setTechOpen(!techOpen)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="font-medium text-slate-500 text-sm">Technische Details</h2>
                {techOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
              {techOpen && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Code</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{group.code}</code>
                      <button
                        onClick={() => copyToClipboard(group.code)}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        title="Code kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">ID</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded text-xs">{group.id}</code>
                      <button
                        onClick={() => copyToClipboard(group.id)}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        title="ID kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Delete Section (nur bei Edit, nicht bei Sammelposten in Phase 1) */}
        {!isNew && group && !isSystemBucketMode && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren einer Kundengruppe entfernt sie aus der Auswahl. 
              Bereits zugeordnete Kunden behalten ihre Gruppenzugehörigkeit.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Kundengruppe deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Kundengruppe deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{group?.name}"</strong> ({group?.code}) wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {group && group.customers.length > 0 
                  ? `${group.customers.length} Kunden sind dieser Gruppe zugeordnet.`
                  : 'Keine Kunden zugeordnet.'}
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
